import { useEffect, useMemo, useState } from 'react'
import { FaSearch, FaSlidersH, FaTimes } from 'react-icons/fa'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import api from 'services/api'
import { trackEvent } from 'services/analytics'
import CityModal from 'components/cityModal'
import { ModelCity } from 'models/ModelCity'

import { formatInterest, TRAVEL_INTEREST_VALUES } from 'travelInterests'
import { findDestinationCatalogState } from 'utils/destinationCatalog'

import * as S from './style'

type RuntimeInterest = string | { name?: string }

type WikipediaPage = {
  original?: { source?: string }
  thumbnail?: { source?: string }
}

type WikipediaGeoResult = {
  pageid?: number
  title?: string
}

type DestinationCatalogState = {
  destinationKey: string
  sourceName: string
  latitude: number
  longitude: number
  slug?: string | null
  displayName?: string | null
  status: 'active' | 'hidden'
  featured: boolean
  description?: string | null
  seoTitle?: string | null
  seoDescription?: string | null
  coverImageUrl?: string | null
  countryName?: string | null
}

type DestinationCatalogItem = {
  key: string
  city: ModelCity
  routeCount: number
  image?: string | null
  interests: string[]
}

type Destination = {
  key: string
  name: string
  city: ModelCity
  routeCount: number
  image: string
  interests: string[]
  slug?: string
  featured?: boolean
}

const FALLBACK_IMAGE = '/images/itinerary-banner.png'
const DESTINATIONS_PER_PAGE = 9

const normalize = (value = '') =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()

const getInterestName = (interest: RuntimeInterest) =>
  typeof interest === 'string' ? interest : interest?.name || ''

const DestinationCover = ({
  name,
  latitude,
  longitude,
  coverImageUrl
}: {
  name: string
  latitude: number
  longitude: number
  coverImageUrl?: string | null
}) => {
  const [image, setImage] = useState(coverImageUrl || FALLBACK_IMAGE)

  useEffect(() => {
    setImage(coverImageUrl || FALLBACK_IMAGE)

    if (coverImageUrl) return

    let cancelled = false
    let attempts = 0
    let timer: ReturnType<typeof setTimeout> | undefined
    const cityName = name.split(',')[0].trim()
    const parsedLatitude = Number(latitude)
    const parsedLongitude = Number(longitude)

    const getGooglePhoto = () => {
      attempts += 1

      if (!window.google?.maps?.places || !window.google?.maps?.Geocoder) {
        if (attempts < 20) timer = setTimeout(getGooglePhoto, 250)
        return
      }

      const hasCoordinates =
        Number.isFinite(parsedLatitude) && Number.isFinite(parsedLongitude)
      const location = hasCoordinates
        ? new window.google.maps.LatLng(parsedLatitude, parsedLongitude)
        : undefined

      const findPhoto = (country?: string) => {
        const service = new window.google.maps.places.PlacesService(
          document.createElement('div')
        )
        const query = [cityName, country].filter(Boolean).join(', ')
        const request: any = {
          query,
          fields: ['name', 'photos', 'geometry']
        }

        if (location) request.locationBias = location

        service.findPlaceFromQuery(request, (results, status) => {
          if (cancelled) return

          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            results?.[0]?.photos?.[0]
          ) {
            setImage(
              results[0].photos[0].getUrl({
                maxWidth: 1800,
                maxHeight: 900
              })
            )
          }
        })
      }

      if (!location) {
        findPhoto()
        return
      }

      const geocoder = new window.google.maps.Geocoder()
      geocoder.geocode({ location }, (results, status) => {
        if (cancelled) return

        if (status !== 'OK' || !results?.length) {
          findPhoto()
          return
        }

        const country = results
          .flatMap((result) => result.address_components || [])
          .find((component) => component.types.includes('country'))?.long_name

        findPhoto(country)
      })
    }

    const getWikipediaPhoto = async () => {
      const fetchPage = async (language: 'pt' | 'en') => {
        let pageId: number | undefined

        if (
          Number.isFinite(parsedLatitude) &&
          Number.isFinite(parsedLongitude)
        ) {
          try {
            const nearbyResult = await axios.get(
              `https://${language}.wikipedia.org/w/api.php`,
              {
                params: {
                  action: 'query',
                  list: 'geosearch',
                  gscoord: `${parsedLatitude}|${parsedLongitude}`,
                  gsradius: 10000,
                  gslimit: 15,
                  format: 'json',
                  origin: '*'
                }
              }
            )

            const nearbyPages = (nearbyResult.data?.query?.geosearch ||
              []) as WikipediaGeoResult[]
            const normalizedCity = normalize(cityName)
            const selected =
              nearbyPages.find(
                (item) => normalize(item.title || '') === normalizedCity
              ) ||
              nearbyPages.find((item) =>
                normalize(item.title || '').startsWith(normalizedCity)
              ) ||
              nearbyPages[0]

            pageId = selected?.pageid
          } catch {
            // Se a busca geográfica falhar, tentamos pelo título abaixo.
          }
        }

        const result = await axios.get(
          `https://${language}.wikipedia.org/w/api.php`,
          {
            params: {
              action: 'query',
              prop: 'pageimages',
              redirects: 1,
              piprop: 'original|thumbnail',
              pithumbsize: 1800,
              ...(pageId ? { pageids: pageId } : { titles: cityName }),
              format: 'json',
              origin: '*'
            }
          }
        )

        const pages = result.data?.query?.pages || {}
        return Object.values(pages)[0] as WikipediaPage | undefined
      }

      try {
        const portuguesePage = await fetchPage('pt')
        const portugueseImage =
          portuguesePage?.original?.source || portuguesePage?.thumbnail?.source

        if (portugueseImage) {
          if (!cancelled) setImage(portugueseImage)
          return
        }

        const englishPage = await fetchPage('en')
        const englishImage =
          englishPage?.original?.source || englishPage?.thumbnail?.source

        if (englishImage) {
          if (!cancelled) setImage(englishImage)
          return
        }

        if (!cancelled) getGooglePhoto()
      } catch {
        // Se a Wikipedia não responder, usamos Google Places como fallback.
        if (!cancelled) getGooglePhoto()
      }
    }

    getWikipediaPhoto()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [coverImageUrl, latitude, longitude, name])

  return <S.CardImage $image={image} />
}

const Destinations = () => {
  const history = useHistory()
  const location = useLocation()
  const [search, setSearch] = useState('')
  const [selectedInterests, setSelectedInterests] = useState<string[]>([])
  const [sort, setSort] = useState<'routes' | 'az' | 'za'>('routes')
  const [selectedCity, setSelectedCity] = useState<ModelCity | null>(null)
  const [visibleCount, setVisibleCount] = useState(DESTINATIONS_PER_PAGE)
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)

  useEffect(() => {
    const query = new URLSearchParams(location.search)
    const legacyCity = query.get('city')
    if (legacyCity) {
      history.replace(`/roteiros?city=${encodeURIComponent(legacyCity)}`)
    }
  }, [history, location.search])

  useEffect(() => {
    if (!isMobileFiltersOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMobileFiltersOpen(false)
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isMobileFiltersOpen])

  const { data: catalogState = [] } = useQuery({
    queryKey: ['destination-catalog-state'],
    queryFn: async () => {
      const { data } = await api.get<DestinationCatalogState[]>(
        '/cities/catalog-state'
      )
      return data ?? []
    },
    staleTime: 0,
    refetchOnMount: 'always'
  })

  const { data: catalogItems = [], isLoading } = useQuery({
    queryKey: ['destination-catalog-summary'],
    queryFn: async () => {
      const { data } = await api.get<DestinationCatalogItem[]>(
        '/itineraries/destination-catalog'
      )
      return data ?? []
    },
    staleTime: 0,
    refetchOnMount: 'always'
  })

  const destinations = useMemo<Destination[]>(() => {
    // Treat published destination states as the source of truth, including
    // localities without public itineraries. Itinerary data enriches cards.
    const legacyEntries = catalogItems.map((item) => ({
      item,
      state: findDestinationCatalogState(catalogState, item.city)
    }))

    return catalogState
      .filter(
        (state) =>
          state.status === 'active' &&
          Boolean(
            state.slug &&
              String(state.displayName || state.sourceName || '').trim()
          ) &&
          Number.isFinite(Number(state.latitude)) &&
          Number.isFinite(Number(state.longitude)) &&
          !(Number(state.latitude) === 0 && Number(state.longitude) === 0)
      )
      .map((state): Destination => {
        const item = legacyEntries.find(
          (entry) => entry.state?.destinationKey === state.destinationKey
        )?.item
        const name = String(state.displayName || state.sourceName).trim()
        const city = {
          ...(item?.city || {}),
          id: item?.city?.id || state.destinationKey,
          name,
          latitude: Number(state.latitude),
          longitude: Number(state.longitude),
          description: state.description || item?.city?.description
        } as ModelCity

        return {
          key: state.destinationKey,
          name,
          city,
          routeCount: Number(item?.routeCount || 0),
          image: state.coverImageUrl || item?.image || '',
          interests: item?.interests || [],
          slug: state.slug || undefined,
          featured: Boolean(state.featured)
        }
      })
  }, [catalogItems, catalogState])

  const availableInterests = TRAVEL_INTEREST_VALUES

  const filteredDestinations = useMemo(() => {
    const normalizedSearch = normalize(search)

    const result = destinations.filter((destination) => {
      const matchesSearch =
        !normalizedSearch ||
        normalize(destination.name).includes(normalizedSearch)
      const matchesInterests =
        selectedInterests.length === 0 ||
        selectedInterests.some((interest) =>
          destination.interests.includes(interest)
        )

      return matchesSearch && matchesInterests
    })

    return result.sort((a, b) => {
      if (sort === 'az') return a.name.localeCompare(b.name, 'pt-BR')
      if (sort === 'za') return b.name.localeCompare(a.name, 'pt-BR')
      return (
        Number(Boolean(b.featured)) - Number(Boolean(a.featured)) ||
        b.routeCount - a.routeCount ||
        a.name.localeCompare(b.name, 'pt-BR')
      )
    })
  }, [destinations, search, selectedInterests, sort])

  useEffect(() => {
    setVisibleCount(DESTINATIONS_PER_PAGE)
  }, [search, selectedInterests, sort])

  const visibleDestinations = filteredDestinations.slice(0, visibleCount)
  const hasMoreDestinations = visibleCount < filteredDestinations.length

  useEffect(() => {
    const term = search.trim()
    if (!term) return

    const timer = window.setTimeout(() => {
      trackEvent({
        eventType: 'search',
        entityType: 'destination',
        value: term,
        metadata: {
          scope: 'destinations',
          resultCount: filteredDestinations.length
        }
      })
    }, 700)

    return () => window.clearTimeout(timer)
  }, [filteredDestinations.length, search])

  const toggleInterest = (interest: string) => {
    setSelectedInterests((current) =>
      current.includes(interest)
        ? current.filter((item) => item !== interest)
        : [...current, interest]
    )
  }

  const clearFilters = () => {
    setSearch('')
    setSelectedInterests([])
    setSort('routes')
  }

  return (
    <S.Container>
      <S.Hero>
        <S.HeroInner>
          <S.Eyebrow>Descubra o mundo pela comunidade</S.Eyebrow>
          <S.Title>
            Explore <strong>destinos</strong>
          </S.Title>
          <S.Subtitle>
            Explore cidades da RedSterna e abra a ficha do destino para ver
            informações locais, experiências e roteiros relacionados.
          </S.Subtitle>
          <S.SearchBar>
            <FaSearch />
            <S.SearchInput
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar cidade..."
              aria-label="Buscar destino"
            />
          </S.SearchBar>
        </S.HeroInner>
      </S.Hero>

      <S.Content>
        <S.Filters>
          <S.FilterHeading>
            <div>
              <FaSlidersH />
              <S.FilterTitle>Filtrar por interesses</S.FilterTitle>
            </div>
          </S.FilterHeading>
          <S.FilterHint>
            Mostra cidades ligadas a roteiros com pelo menos um dos interesses
            selecionados.
          </S.FilterHint>
          <S.InterestList>
            {availableInterests.map((interest) => (
              <S.InterestLabel key={interest}>
                <input
                  type="checkbox"
                  checked={selectedInterests.includes(interest)}
                  onChange={() => toggleInterest(interest)}
                />
                <span>{formatInterest(interest)}</span>
              </S.InterestLabel>
            ))}
          </S.InterestList>
          {(search || selectedInterests.length > 0 || sort !== 'routes') && (
            <S.ClearButton type="button" onClick={clearFilters}>
              Limpar filtros
            </S.ClearButton>
          )}
        </S.Filters>

        {isMobileFiltersOpen && (
          <>
            <S.MobileFilterOverlay
              role="presentation"
              onMouseDown={() => setIsMobileFiltersOpen(false)}
            />
            <S.MobileFilters
              role="dialog"
              aria-modal="true"
              aria-label="Filtrar destinos"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <S.FilterHeading>
                <div>
                  <FaSlidersH />
                  <S.FilterTitle>Filtrar por interesses</S.FilterTitle>
                </div>
                <S.MobileFilterClose
                  type="button"
                  aria-label="Fechar filtros"
                  onClick={() => setIsMobileFiltersOpen(false)}
                >
                  <FaTimes />
                </S.MobileFilterClose>
              </S.FilterHeading>

              <S.FilterHint>
                Mostra cidades ligadas a roteiros com pelo menos um dos
                interesses selecionados.
              </S.FilterHint>

              <S.InterestList>
                {availableInterests.map((interest) => (
                  <S.InterestLabel key={interest}>
                    <input
                      type="checkbox"
                      checked={selectedInterests.includes(interest)}
                      onChange={() => toggleInterest(interest)}
                    />
                    <span>{formatInterest(interest)}</span>
                  </S.InterestLabel>
                ))}
              </S.InterestList>

              <S.FilterActions>
                <S.ClearButton
                  type="button"
                  onClick={clearFilters}
                  disabled={
                    !search &&
                    selectedInterests.length === 0 &&
                    sort === 'routes'
                  }
                >
                  Limpar
                </S.ClearButton>
                <S.ApplyFiltersButton
                  type="button"
                  onClick={() => setIsMobileFiltersOpen(false)}
                >
                  Aplicar filtros
                </S.ApplyFiltersButton>
              </S.FilterActions>
            </S.MobileFilters>
          </>
        )}

        <S.Results>
          <S.MobileResultsToolbar>
            <S.MobileFilterButton
              type="button"
              onClick={() => setIsMobileFiltersOpen(true)}
            >
              <FaSlidersH />
              Filtrar
              {selectedInterests.length > 0 && (
                <strong>{selectedInterests.length}</strong>
              )}
            </S.MobileFilterButton>

            <S.MobileSortSelect
              value={sort}
              onChange={(event) =>
                setSort(event.target.value as 'routes' | 'az' | 'za')
              }
              aria-label="Ordenar destinos"
            >
              <option value="routes">Mais roteiros</option>
              <option value="az">A–Z</option>
              <option value="za">Z–A</option>
            </S.MobileSortSelect>
          </S.MobileResultsToolbar>

          {selectedInterests.length > 0 && (
            <S.ActiveFilters>
              {selectedInterests.map((interest) => (
                <button
                  type="button"
                  key={interest}
                  onClick={() => toggleInterest(interest)}
                >
                  {formatInterest(interest)} ×
                </button>
              ))}
            </S.ActiveFilters>
          )}
          <S.ResultsHeader>
            <S.ResultCount>
              <strong>{filteredDestinations.length} destinos</strong>
              <span>
                {isLoading
                  ? 'Carregando cidades...'
                  : 'Clique em um destino para abrir sua ficha'}
              </span>
            </S.ResultCount>
            <S.SortSelect
              value={sort}
              onChange={(event) =>
                setSort(event.target.value as 'routes' | 'az' | 'za')
              }
              aria-label="Ordenar destinos"
            >
              <option value="routes">Mais roteiros</option>
              <option value="az">A–Z</option>
              <option value="za">Z–A</option>
            </S.SortSelect>
          </S.ResultsHeader>

          {filteredDestinations.length > 0 ? (
            <>
              <S.Grid>
                {visibleDestinations.map((destination) => (
                  <S.Card
                    key={destination.key}
                    as="a"
                    href={
                      destination.slug
                        ? `/destinos/${destination.slug}`
                        : undefined
                    }
                    onClick={(event: any) => {
                      event.preventDefault()
                      trackEvent({
                        eventType: 'destination_view',
                        entityType: 'destination',
                        entityId: destination.key,
                        value: destination.name
                      })
                      if (destination.slug) {
                        history.push(`/destinos/${destination.slug}`)
                        return
                      }

                      setSelectedCity(destination.city)
                    }}
                  >
                    <DestinationCover
                      name={destination.name}
                      latitude={Number(destination.city.latitude)}
                      longitude={Number(destination.city.longitude)}
                      coverImageUrl={destination.image || undefined}
                    />
                    <S.CardBody>
                      <S.CardTop>
                        <S.CityName>
                          {destination.name}
                          {destination.featured && <S.Chip>Em destaque</S.Chip>}
                        </S.CityName>
                        <S.RouteCount>
                          {destination.routeCount}{' '}
                          {destination.routeCount === 1
                            ? 'roteiro'
                            : 'roteiros'}
                        </S.RouteCount>
                      </S.CardTop>
                      {destination.interests.length > 0 && (
                        <S.InterestChips>
                          {destination.interests.slice(0, 3).map((interest) => (
                            <S.Chip key={interest}>
                              {formatInterest(interest)}
                            </S.Chip>
                          ))}
                        </S.InterestChips>
                      )}
                    </S.CardBody>
                  </S.Card>
                ))}
              </S.Grid>

              <S.LoadMoreArea>
                <S.LoadMoreStatus>
                  Exibindo {visibleDestinations.length} de{' '}
                  {filteredDestinations.length} destinos
                </S.LoadMoreStatus>
                {hasMoreDestinations && (
                  <S.LoadMoreButton
                    type="button"
                    onClick={() =>
                      setVisibleCount(
                        (current) => current + DESTINATIONS_PER_PAGE
                      )
                    }
                  >
                    Ver mais destinos
                  </S.LoadMoreButton>
                )}
              </S.LoadMoreArea>
            </>
          ) : (
            !isLoading && (
              <S.EmptyState>
                Nenhum destino encontrado com os filtros selecionados.
              </S.EmptyState>
            )
          )}
        </S.Results>
      </S.Content>

      {selectedCity && (
        <CityModal
          isOpen={Boolean(selectedCity)}
          city={selectedCity}
          closeModal={() => setSelectedCity(null)}
        />
      )}
    </S.Container>
  )
}

export default Destinations
